import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import { FilterConfig } from 'hooks/useListQuery';
import { RetentionType } from 'utils/retentionPolicies';

export interface RetentionPolicyRule {
  status: string; // 'all' | 'completed' | 'failed' | 'canceled' | 'skipped' | 'terminated' | 'error'
  retention_type: RetentionType;
  value: number; // >= 0
}

export interface Zone {
  zone_id: number;
  name: string;
  new_name?: string;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
  retention_policies?: RetentionPolicyRule[];
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface ZonesState {
  zones: Zone[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  auditTrail: any;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    getZones: boolean;
    getAuthorizedZones: boolean;
    getZonesPaginated: boolean;
    createZone: boolean;
    updateZone: boolean;
    deleteZone: boolean;
    getZoneAuditTrail: boolean;
  };
}

const initialState: ZonesState = {
  zones: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  auditTrail: null,
  config: [],
  filtersConfig: [],
  loading: {
    getZones: false,
    getAuthorizedZones: false,
    getZonesPaginated: false,
    createZone: false,
    updateZone: false,
    deleteZone: false,
    getZoneAuditTrail: false,
  },
};

// Fetch Zones (non-paginated)
export const getZones = createAsyncThunk<{ zones: Zone[] }>('zones/getZones', async () => {
  const response = await api.get('/zones', {
    params: { limit: initialState.pagination.pageSize },
  });
  return response.data;
});

export const getAuthorizedZones = createAsyncThunk('zones/getAuthorizedZones', async (args?: { resourceType?: string; action?: string }) => {
  const params: any = { authorized: 'true' };

  if (args?.resourceType) {
    params.resource_type = args.resourceType;
    if (args.action) {
      params.action = args.action;
    }
  }

  const response = await api.get('/lists/zones', { params });
  return response.data;
});

// Fetch Zones (paginated)
export const getZonesPaginated = createAsyncThunk('zones/getZonesPaginated', async (params: Record<string, any>) => {
  const queryParams = {
    limit: initialState.pagination.pageSize,
    ...params,
  };
  const response = await api.get('/zones', {
    params: flattenQuery(queryParams),
  });
  return response.data;
});

// Create Zone
export const createZone = createAsyncThunk<
  Zone,
  {
    name: string;
    description: string;
    retention_policies?: RetentionPolicyRule[];
  },
  { rejectValue: { message: string; error?: string; queues?: any } }
>('zones/createZone', async (zoneData, { rejectWithValue }) => {
  try {
    const response = await api.post('/zones', zoneData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

// Update Zone
export const updateZone = createAsyncThunk<
  Zone,
  {
    zone_id: number;
    name: string;
    new_name?: string;
    description: string;
    retention_policies?: RetentionPolicyRule[];
  }
>('zones/updateZone', async (zoneData) => {
  const response = await api.put(`/zones/${zoneData.name}`, zoneData);
  return response.data;
});

// Delete Zone
export const deleteZone = createAsyncThunk<{ message: string }, string, { rejectValue: { message: string; error?: string; queues?: any } }>(
  'zones/deleteZone',
  async (name, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/zones/${name}`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

// Get Zone Audit Trail
export const getZoneAuditTrail = createAsyncThunk<any, string>('zones/getZoneAuditTrail', async (name) => {
  const response = await api.get(`/zones/audit_trail/${name}`);
  return response.data;
});

const zonesSlice = createSlice({
  name: 'zones',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ================== getZones ==================
      .addCase(getZones.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getZones = true;
      })
      .addCase(getZones.fulfilled, (state, action) => {
        state.zones = action.payload.zones;
        state.status = 'succeeded';
        state.loading.getZones = false;
      })
      .addCase(getZones.rejected, (state, action) => {
        state.status = 'failed';
        state.loading.getZones = false;
        state.error = action.error.message || 'Failed to fetch zones';
      })
      .addCase(getAuthorizedZones.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getAuthorizedZones = true;
      })
      .addCase(getAuthorizedZones.fulfilled, (state, action) => {
        state.zones = action.payload.zones;
        state.status = 'succeeded';
        state.loading.getAuthorizedZones = false;
      })
      .addCase(getAuthorizedZones.rejected, (state, action) => {
        state.status = 'failed';
        state.loading.getAuthorizedZones = false;
        state.error = action.error.message || 'Failed to fetch authorized zones';
      });

    builder
      .addCase(getZonesPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getZonesPaginated = true;
      })
      .addCase(getZonesPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { zones, config, filtersConfig, pagination } = action.payload;

        state.zones = zones;
        state.config = config;
        state.filtersConfig = filtersConfig || [];

        state.pagination.totalItems = pagination?.total_records ?? 0;
        state.pagination.totalPages = pagination?.total_pages ?? 0;
        state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
        state.pagination.currentPage = (pagination?.current_page ?? 1) > (pagination?.total_pages ?? 0) ? 1 : (pagination?.current_page ?? 1);

        state.loading.getZonesPaginated = false;
      })
      .addCase(getZonesPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch zones';
        state.loading.getZonesPaginated = false;
      });

    builder
      // ================== createZone ==================
      .addCase(createZone.pending, (state) => {
        state.loading.createZone = true;
        state.error = null;
      })
      .addCase(createZone.fulfilled, (state, action) => {
        state.zones.push(action.payload);
        state.status = 'succeeded';
        state.loading.createZone = false;

        const toastId = 'data-created-zone';
        if (!toast.isActive(toastId)) {
          toast.success(`Zone created successfully!`, { toastId });
        }
      })
      .addCase(createZone.rejected, (state, action) => {
        state.loading.createZone = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string; queues?: any } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to create zone';

        const toastId = 'data-failed-to-create-zone';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to create new zone! ${state.error}`, { toastId });
        }
      })

      // ================== updateZone ==================
      .addCase(updateZone.pending, (state) => {
        state.loading.updateZone = true;
        state.error = null;
      })
      .addCase(updateZone.fulfilled, (state, action) => {
        const index = state.zones.findIndex((zone) => zone.zone_id === action.payload.zone_id);
        if (index !== -1) {
          state.zones[index] = action.payload;
        }
        state.status = 'succeeded';
        state.loading.updateZone = false;

        const toastId = 'data-updated-zone';
        if (!toast.isActive(toastId)) {
          toast.success(`Zone updated successfully!`, { toastId });
        }
      })
      .addCase(updateZone.rejected, (state, action) => {
        state.loading.updateZone = false;
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update zone';

        const toastId = 'data-failed-to-update-zone';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to update the zone. Message: ${state.error}`, {
            toastId,
          });
        }
      })

      // ================== deleteZone ==================
      .addCase(deleteZone.pending, (state) => {
        state.loading.deleteZone = true;
        state.error = null;
      })
      .addCase(deleteZone.fulfilled, (state, action) => {
        // action.meta.arg is the zone name passed to deleteZone
        state.zones = state.zones.filter((zone) => zone.name !== action.meta.arg);
        state.status = 'succeeded';
        state.loading.deleteZone = false;

        const toastId = 'data-deleted-zone';
        if (!toast.isActive(toastId)) {
          toast.success(`Zone deleted successfully!`, { toastId });
        }
      })
      .addCase(deleteZone.rejected, (state, action) => {
        state.loading.deleteZone = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string; queues?: any } | undefined;

        state.error = payload?.error || `${payload?.message || 'Failed to delete zone.'} ${payload?.queues || ''}`;

        const toastId = 'data-failed-to-delete-zone';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to delete the zone! ${state.error}`, { toastId });
        }
      })

      // ================== getZoneAuditTrail ==================
      .addCase(getZoneAuditTrail.pending, (state) => {
        state.loading.getZoneAuditTrail = true;
        // don't necessarily override status here, audit is secondary
      })
      .addCase(getZoneAuditTrail.fulfilled, (state, action) => {
        state.loading.getZoneAuditTrail = false;
        state.status = 'succeeded';
        state.auditTrail = action.payload;
      })
      .addCase(getZoneAuditTrail.rejected, (state, action) => {
        state.loading.getZoneAuditTrail = false;
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch zone audit trail';
      });
  },
});

export default zonesSlice.reducer;
